import { useForm } from '@mantine/form';
import { useNavigate } from 'react-router-dom';
import { Paper } from '@mantine/core';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSaveMutation } from '@/services/api';
import { updateExtraDetails } from '@/data/features/proposal/proposal';
import validate from '@/helpers/validations';
import { getMutationResult } from '@/helpers/query';
import { useTranslation } from 'react-i18next';
import { Page, ProcessHeader, YesNoToggle } from '@/components';
import { getRouteById, routeWithParams } from '@/helpers/routes';
import routes from '@/routes';
import { notifications } from '@mantine/notifications';
import { useIdInStoreGuard } from '@/hooks/useIdfromUrl';
import ProcessButton from '@/components/ProcessButton';
import ChequeIcon from '@/assets/images/cheque.svg?react'; // eslint-disable-line import/no-unresolved

const ExistingMortgageQuestion = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [apiSave] = useSaveMutation();
  const navigate = useNavigate();
  const { extraDetails, id } = useSelector((state) => state.proposal);
  useIdInStoreGuard();

  const initialValues = {
    hasExistingMortgage: extraDetails.isRecycling ?? null,
  };

  const form = useForm({
    mode: 'controlled',
    validateInputOnBlur: true,
    initialValues,
    validate: {
      hasExistingMortgage: (value) => validate(value, { required: t('globals.validations.required') }),
    },
    validateInputOnSubmit: true,
  });

  const handleContinue = async () => {
    try {
      setLoading(true);

      const validation = form.validate();
      if (validation.hasErrors) return;

      const isRecycling = form.values.hasExistingMortgage === 'yes';
      const objectToSend = {
        isRecycling,
        id,
      };

      const { isError } = await getMutationResult(objectToSend, apiSave);
      if (isError) {
        notifications.show({
          title: t('globals.error.title'),
          message: t('globals.error.message'),
        });
        return;
      }

      dispatch(updateExtraDetails({ isRecycling: form.values.hasExistingMortgage }));

      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'assetValue').path, { id }));
    } catch (error) {
      console.error('Error saving data:', error);
      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReturn = () => {
    navigate(
      routeWithParams(getRouteById(routes.newProposal.id, 'reason').path, { id }),
    );
  };

  return (
    <ProcessHeader
      headerText="משכנתא קיימת"
      headerColor="#FFE6F0"
      headerTextColor="#E91E63"
      subtitle="בשלב זה נבדקת קיומה של משכנתא קיימת על הנכס"
      showReturnButton
      onReturn={handleReturn}
      icon={ChequeIcon}
    >
      <form onSubmit={form.onSubmit(handleContinue)}>
        <Paper>
          <YesNoToggle
            name="hasExistingMortgage"
            label="האם קיימת הלוואת משכנתא על הנכס הקיים?"
            value={form.values.hasExistingMortgage}
            onChange={(value) => form.setFieldValue('hasExistingMortgage', value)}
            required
            error={form.errors.hasExistingMortgage}
            yesColor="#E91E63"
            noColor="#4CAF50"
          />
        </Paper>

        <Page.Actions>
          <ProcessButton type="submit" loading={loading} />
        </Page.Actions>
      </form>
    </ProcessHeader>
  );
};

export default ExistingMortgageQuestion;
